import React, { useState, useEffect, useCallback, memo } from 'react';
import { IoClose as Close } from 'react-icons/io5';
import LabelIcon from '@mui/icons-material/Label';
import { BsPersonCircle as ProfilePicture } from 'react-icons/bs';
import { BsBuilding as Organizations } from 'react-icons/bs';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Modal from '@mui/material/Modal';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LabelCardService from '@Helpers/URLHelpers/WeboutService/LabelCardService';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import OrganizationCardService from '@Helpers/URLHelpers/WeboutService/OrganizationCardService';
import ErrorHandler from '@Root/ErrorHandler';
import './LabelClickUpMenu.scss';

function LabelClickUpMenu(props) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [labelData, setLabelData] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const className = 'labelClickUpMenu';
  const errorHandler = new ErrorHandler();

  const labelCardService = new LabelCardService();
  const nameCardService = new NameCardService();
  const organizationCardService = new OrganizationCardService();

  // Deterministic color
  const getBaseColor = useCallback((name, id) => {
    const palette = [
      '#2784B9','#40c47d','#FDB16C','#b06060','#f77474','#6080b0','#9a60b0','#6cb060',
      '#b0ae60','#65412b','#cc9963','#e1bd83','#60748b','#744700','#c8d579','#5f8fba',
      '#3e6073','#8e6dc1','#473785','#9158a9','#7f3f7a','#583e84','#073763','#20124d',
      '#3d124d','#64113f','#780448','#003e23','#108764','#1a4c3d','#c8d77f','#11888b',
      '#1a95bd','#0d6c7b','#25b56e','#006e3d'
    ];
    if (!name) return '#60748b';
    let hash = 0;
    const str = `${name}${id || ''}`;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
  }, []);

  const getInitials = (value) => {
    if (!value) return 'U';
    const parts = value.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const toFile = (dataUrl) => {
    try {
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) u8arr[n] = bstr.charCodeAt(n);
      return new File([u8arr], 'img', { type: mime });
    } catch {
      return null;
    }
  };

  const fetchLabelData = useCallback(async () => {
    const link = props.labelLink || props.history;
    if (!link) return;
    setLoading(true);
    let response;
    try {
      switch (link.registryType) {
        case 'labelCard':
          response = await labelCardService.getById(link.registryId);
          break;
        case 'nameCard':
          response = await nameCardService.getById(link.registryId);
          break;
        case 'orgCard':
          response = await organizationCardService.getById(link.registryId);
          break;
        default:
          break;
      }
      const parsed = JSON.parse(response?.body || '{}');
      setLabelData(parsed);
      if (parsed?.photo && parsed.photo !== 'N/A') {
        const fileObj = toFile(parsed.photo);
        if (fileObj) setImageUrl(URL.createObjectURL(fileObj));
      } else {
        setImageUrl('');
      }
    } catch (err) {
      errorHandler.handleError(err);
    }
    setLoading(false);
  }, [labelCardService, nameCardService, organizationCardService, props.labelLink, props.history]);

  useEffect(() => {
    if (expanded) {
      fetchLabelData();
    }
  }, [expanded]);

  // Preload if history is passed (read-only mode)
  useEffect(() => {
    if (props.history && !labelData) fetchLabelData();
  }, [props.history, labelData, fetchLabelData]);

  const handleToggle = () => {
    setExpanded((v) => !v);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (props.handleDeleteLabelRegistryClick) {
      props.handleDeleteLabelRegistryClick(props.labelLink.id);
    }
  };

  const link = props.labelLink || props.history;
  if (!link) return null;

  const baseColor = getBaseColor(link.name, link.registryId);
  const showAvatar = link.registryType === 'nameCard' || link.registryType === 'orgCard';
  const avatarNode =
    showAvatar &&
    (link.photo && link.photo !== 'N/A'
      ? <Avatar src={URL.createObjectURL(toFile(link.photo))} />
      : <Avatar sx={{ bgcolor: baseColor }}>{getInitials(link.name)}</Avatar>);

  return (
    <>
      <Chip
        aria-label={`Label ${link.name}`}
        label={
          <div className={className + '__chipLabel'}>
            <span className={className + '__chipLabel__text'}>{link.name}</span>
          </div>
        }
        size="small"
        variant="filled"
        avatar={avatarNode || undefined}
        onClick={handleToggle}
        onDelete={props.handleDeleteLabelRegistryClick ? handleDelete : undefined}
        sx={{
          bgcolor: baseColor + ' !important',
          color: '#fff !important',
          fontWeight: 600,
          '.MuiChip-label': { px: 0.75 }
        }}
        className={className + '__chip'}
      />

      <Modal
        open={expanded}
        onClose={handleToggle}
        aria-labelledby="label-detail-title"
        className={className + '__modal'}
      >
        <Box className={className + '__modal__container'}>
          <Box className={className + '__modal__header'}>
            <Box className={className + '__modal__title'}>
              {link.registryType === 'labelCard' && <LabelIcon className={className + '__modal__icon'} />}
              {link.registryType === 'nameCard' && (
                imageUrl
                  ? <Avatar src={imageUrl} className={className + '__modal__avatar'} />
                  : <ProfilePicture className={className + '__modal__icon'} />
              )}
              {link.registryType === 'orgCard' && (
                imageUrl
                  ? <Avatar src={imageUrl} className={className + '__modal__avatar'} />
                  : <Organizations className={className + '__modal__icon'} />
              )}
              <Typography id="label-detail-title" className={className + '__modal__titleText'}>
                {labelData?.name || link.name}
              </Typography>
            </Box>
            <Close className={className + '__modal__close'} onClick={handleToggle} />
          </Box>

            {loading && (
              <Box className={className + '__modal__loading'}>
                <LinearProgress className={className + '__modal__progress'} />
                <Typography className={className + '__modal__loadingText'}>Loading...</Typography>
              </Box>
            )}

            {!loading && labelData && (
              <Box className={className + '__modal__body'}>
                <Box className={className + '__modal__section'}>
                  <Typography className={className + '__modal__sectionTitle'}>Meta</Typography>
                  <Box className={className + '__modal__grid'}>
                    <div>
                      <span className={className + '__modal__fieldLabel'}>Created</span>
                      <span className={className + '__modal__fieldValue'}>
                        {labelData?.createTimeStamp
                          ? new Date(labelData.createTimeStamp).toLocaleString()
                          : '-'}
                      </span>
                    </div>
                    <div>
                      <span className={className + '__modal__fieldLabel'}>Updated</span>
                      <span className={className + '__modal__fieldValue'}>
                        {labelData?.modifiedTimeStamp
                          ? new Date(labelData.modifiedTimeStamp).toLocaleString()
                          : '-'}
                      </span>
                    </div>
                  </Box>
                </Box>

                <Box className={className + '__modal__section'}>
                  <Typography className={className + '__modal__sectionTitle'}>Details</Typography>
                  <Box className={className + '__modal__details'}>
                    {[
                      'email','designation','department','branch','contactNumber','address','pinCode',
                      'country','state','industryType','owner','poc','remarks'
                    ]
                      .filter((k) => !!labelData[k])
                      .map((k) => (
                        <div key={k} className={className + '__modal__detailRow'}>
                          <span className={className + '__modal__fieldLabel'}>{toTitle(k)}</span>
                          <span className={className + '__modal__fieldValue'}>{labelData[k]}</span>
                        </div>
                      ))}
                    {(![
                      'email','designation','department','branch','contactNumber','address','pinCode','country','state',
                      'industryType','owner','poc','remarks'
                    ].some((k) => !!labelData[k])) && (
                      <Typography className={className + '__modal__empty'}>No additional data</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
        </Box>
      </Modal>
    </>
  );
}

function toTitle(str) {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (m) => m.toUpperCase())
    .trim();
}

export default memo(LabelClickUpMenu);
